import { EFFECT_PARAMS, EFFECT_TYPES, type EffectType } from '@cyberscore/contracts';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { move } from './journey';

export type EffectDraft = { type: EffectType; params: Record<string, unknown> };

/** Starting parameters for each effect, so a new one is nearly valid. */
export const EFFECT_TEMPLATES: Record<EffectType, Record<string, unknown>> = {
  score_change: { control: '', implementation: 0 },
  create_risk: { risk: '' },
  create_finding: { control: '', severity: 'high' },
  ask_question: { question: '' },
  skip_questions: { questions: [] },
  start_workflow: { workflow: '' },
  complete_workflow: { workflow: '' },
  request_evidence: { document_type: '' },
  create_graph_entity: { entity: '' },
  create_relationship: { relationship: '' },
  create_task: { task: '' },
  create_reminder: { offsets: ['T-120', 'T-90', 'T-60', 'T-30', 'T-7'] },
  send_notification: { to: 'underwriter' },
  require_human_review: { reason: '' },
  invoke_ai: { agent: '' },
  call_external_api: { connector: '' },
};

export const effectLabel = (t: string) => t.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

/** What each effect's parameters are wrong about, if anything. */
export function effectProblem(e: EffectDraft): string | null {
  const r = EFFECT_PARAMS[e.type]?.safeParse(e.params);
  if (!r) return `Unknown effect ${e.type}`;
  return r.success
    ? null
    : r.error.issues.map((i) => `${i.path.join('.') || 'params'}: ${i.message}`).join('; ');
}

/** A list of effects (a rule's "then", an option's or a question's effects), parameters as JSON. */
export function EffectsEditor({
  value,
  onChange,
  disabled,
  min = 0,
}: {
  value: EffectDraft[];
  onChange: (v: EffectDraft[]) => void;
  disabled?: boolean;
  min?: number;
}) {
  const set = (i: number, e: EffectDraft) => onChange(value.map((x, j) => (j === i ? e : x)));
  return (
    <div className="effects">
      {value.map((e, i) => (
        <EffectRow
          key={`${i}:${e.type}`}
          e={e}
          disabled={disabled}
          onChange={(x) => set(i, x)}
          onUp={i > 0 ? () => onChange(move(value, i, i - 1)) : undefined}
          onDown={i < value.length - 1 ? () => onChange(move(value, i, i + 1)) : undefined}
          onRemove={value.length > min ? () => onChange(value.filter((_, j) => j !== i)) : undefined}
        />
      ))}
      {!disabled && (
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => onChange([...value, { type: 'create_task', params: { ...EFFECT_TEMPLATES.create_task } }])}>
          <Plus size={14} /> Add an effect
        </button>
      )}
    </div>
  ); // prettier-ignore
}

function EffectRow({
  e,
  disabled,
  onChange,
  onUp,
  onDown,
  onRemove,
}: {
  e: EffectDraft;
  disabled?: boolean;
  onChange: (e: EffectDraft) => void;
  onUp?: () => void;
  onDown?: () => void;
  onRemove?: () => void;
}) {
  const [text, setText] = useState(JSON.stringify(e.params));
  const [jsonError, setJsonError] = useState<string | null>(null);
  const problem = jsonError ?? effectProblem(e);
  return (
    <div className={`effect${problem ? ' bad' : ''}`}>
      <div className="row gap">
        <select
          className="input input-sm"
          aria-label="Effect"
          disabled={disabled}
          value={e.type}
          onChange={(ev) => {
            const type = ev.target.value as EffectType;
            const params = { ...EFFECT_TEMPLATES[type] };
            setText(JSON.stringify(params));
            setJsonError(null);
            onChange({ type, params });
          }}
        >
          {EFFECT_TYPES.map((t) => (
            <option key={t} value={t}>
              {effectLabel(t)}
            </option>
          ))}
        </select>
        <input
          className="input input-sm mono grow"
          aria-label={`${effectLabel(e.type)} parameters (JSON)`}
          disabled={disabled}
          value={text}
          onChange={(ev) => {
            setText(ev.target.value);
            try {
              const params = JSON.parse(ev.target.value);
              if (!params || typeof params !== 'object' || Array.isArray(params)) throw new Error();
              setJsonError(null);
              onChange({ ...e, params });
            } catch {
              setJsonError('Parameters must be a JSON object');
            }
          }}
        />
        {!disabled && (
          <span className="row">
            <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label="Move up" disabled={!onUp} onClick={onUp}>
              <ArrowUp size={13} />
            </button>
            <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label="Move down" disabled={!onDown} onClick={onDown}>
              <ArrowDown size={13} />
            </button>
            <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label="Remove effect" disabled={!onRemove} onClick={onRemove}>
              <Trash2 size={13} />
            </button>
          </span>
        )}
      </div>
      {problem && <div className="error small">{problem}</div>}
    </div>
  ); // prettier-ignore
}
